import { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PageListContainer, PageCreateButton } from "../../../components/Page";

// Data
import { useConfirm } from "../../../components/feedback";
import DataTableWithPagination from "../../../components/DataTable";
import { customerService } from "../../../services/customerService";
import CustomerModel, { getCustomerCols } from "../model";
import CustomerSourceSystemWizardDialog from "./components/CustomerSourceSystemWizardDialog";

function CustomersPage() {
  const { t } = useTranslation();
  const cols = useMemo(() => getCustomerCols(t), [t]);
  const datatable = useRef<any>(null);
  const customerWizardDialogRef = useRef<any>(null);
  const confirm = useConfirm();

  // Global refresh listener - triggered when snackbar closes
  useEffect(() => {
    const handleGlobalRefresh = () => {
      if (datatable.current) {
        datatable.current.refresh();
      }
    };

    window.addEventListener('refreshDataTable', handleGlobalRefresh);
    
    return () => {
      window.removeEventListener('refreshDataTable', handleGlobalRefresh);
    };
  }, []);

  const createNewCustomer = () => {
    if (customerWizardDialogRef.current) {
      customerWizardDialogRef.current.showDialog({ mode: "create" });
    }
  };

  const handleDelete = async (item: any) => {
    return new Promise((resolve) => {
      confirm({ description: t("customers.deleteConfirm", { name: item.name || item.id }) })
        .then(() => {
          customerService.deleteCustomer(item.id).then(() => resolve({}));
        })
        .then(() => {
          CustomerModel
            .message(CustomerModel.SUCCESS, t("common.deleteRequest"), t("customers.deletedSuccess"), Date.now)
            .then((opts) => {
              showSnack(opts);
              setTimeout(() => datatable.current.refresh(), 500);
            });
        })
        .catch((e: unknown) => {
          console.error(e);
        });
    });
  };

  const handleEdit = async (item: any) => {
    if (customerWizardDialogRef.current) {
      customerWizardDialogRef.current.showDialog({ mode: "edit", customer: item });
    }
  };

  const showSnack = (opts: any) => {
    // Use global notification system
    window.dispatchEvent(new CustomEvent('showGlobalNotification', { detail: opts }));
  };

  return (
    <PageListContainer>
      <CustomerSourceSystemWizardDialog ref={customerWizardDialogRef} />

      <DataTableWithPagination
        ref={datatable}
        cols={cols}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        fetchUrl={"/customer/read"}
        searchKey={"name"}
        headerActions={
          <PageCreateButton onClick={createNewCustomer}>{t("customers.new")}</PageCreateButton>
        }
      />
    </PageListContainer>
  );
}

export default CustomersPage;

